// Synthetic tournament data activated via ?test=1. The intent is a fixed
// visual reference for refactors — every weapon type rendered once, the
// known render-edge-cases (unarmed, Martial Arts, Doublehand, Monster,
// missing item), and a few ability-rich units that exercise abilityFormula's
// damage-by-skill switch (Lancer Charges, Wizard elemental magic, Chemist
// potions). The bracket renders with empty winners/maps so the QF cells
// just show the synthetic teams.
//
// buildTestTeams discovers weapon types from the live itemschanged dict
// rather than hardcoding item names, so the page survives item-DB changes
// upstream.

// Which class to assign each weapon type so the unit can actually equip the
// thing (FFT class-weapon affinity). Falls back to Squire for anything not
// listed.
const WEAPON_CLASS_MAP = {
  'Sword':       { class: 'Knight',    gender: 'Male'   },
  'Knight Sword':{ class: 'Knight',    gender: 'Male'   },
  'Katana':      { class: 'Samurai',   gender: 'Male'   },
  'Rod':         { class: 'Wizard',    gender: 'Female' },
  'Spear':       { class: 'Lancer',    gender: 'Male'   },
  'Pole':        { class: 'Oracle',    gender: 'Male'   },
  'Staff':       { class: 'Priest',    gender: 'Female' },
  'Crossbow':    { class: 'Archer',    gender: 'Male'   },
  'Bow':         { class: 'Archer',    gender: 'Male'   },
  'Knife':       { class: 'Thief',     gender: 'Female' },
  'Ninja Blade': { class: 'Ninja',     gender: 'Male'   },
  'Harp':        { class: 'Bard',      gender: 'Male'   },
  'Book':        { class: 'Mediator',  gender: 'Male'   },
  'Fabric':      { class: 'Dancer',    gender: 'Female' },
  'Axe':         { class: 'Squire',    gender: 'Male'   },
  'Bag':         { class: 'Chemist',   gender: 'Female' },
  'Flail':       { class: 'Priest',    gender: 'Male'   },
  'Gun':         { class: 'Chemist',   gender: 'Male'   },
};

function makeUnit(overrides) {
  return {
    Name: 'Test',
    Class: 'Squire',
    Gender: 'Male',
    Sign: 'Aries',
    Brave: 70,
    Faith: 60,
    RaidBoss: false,
    Mainhand: '',
    Offhand: '',
    Head: '',
    Armor: '',
    Accessory: '',
    ClassSkills: [],
    ExtraSkills: [],
    ActionSkill: 'Basic Skill',
    ReactionSkill: 'Counter',
    SupportSkill: '',
    MoveSkill: 'Move+1',
    ...overrides,
  };
}

export function buildTestTeams(itemschanged) {
  // First weapon-of-type we encounter wins — keeps the same item across reloads
  // since itemschanged iteration order is insertion order.
  const sampleByType = new Map();
  let elementalGun = '';
  let nonElementalGun = '';
  for (const item of Object.values(itemschanged)) {
    if (!item.type || !item.wp) continue;
    if (!sampleByType.has(item.type)) sampleByType.set(item.type, item.name);
    if (item.type === 'Gun') {
      if (item.element && !elementalGun) elementalGun = item.name;
      if (!item.element && !nonElementalGun) nonElementalGun = item.name;
    }
  }

  const weaponUnits = [];
  for (const [type, itemName] of sampleByType) {
    if (type === 'Gun') continue; // handled separately so we get both flavors
    const cls = WEAPON_CLASS_MAP[type] || { class: 'Squire', gender: 'Male' };
    weaponUnits.push(makeUnit({
      Name: 'Test_' + type.replace(/\s+/g, ''),
      Class: cls.class,
      Gender: cls.gender,
      Mainhand: itemName,
    }));
  }
  if (nonElementalGun) {
    weaponUnits.push(makeUnit({
      Name: 'Test_Gun', Class: 'Chemist', Gender: 'Male', Mainhand: nonElementalGun,
    }));
  }
  if (elementalGun) {
    weaponUnits.push(makeUnit({
      Name: 'Test_GunElement', Class: 'Chemist', Gender: 'Male', Mainhand: elementalGun,
    }));
  }

  const swordItem = sampleByType.get('Sword') || '';
  const spearItem = sampleByType.get('Spear') || '';
  const rodItem   = sampleByType.get('Rod')   || '';

  const edgeUnits = [
    makeUnit({ Name: 'Test_Unarmed',     Class: 'Monk',        Gender: 'Male'   }),
    makeUnit({ Name: 'Test_MartialArts', Class: 'Monk',        Gender: 'Female', SupportSkill: 'Martial Arts' }),
    makeUnit({ Name: 'Test_Doublehand',  Class: 'Knight',      Gender: 'Male',   Mainhand: swordItem, SupportSkill: 'Doublehand' }),
    makeUnit({ Name: 'Test_Monster',     Class: 'Red Chocobo', Gender: 'Monster' }),
    // Deliberate item-not-in-API name — exercises the safeDict fallback. The
    // unit should still render with zeroed item bonuses, not crash.
    makeUnit({ Name: 'Test_MissingItem', Class: 'Knight',      Gender: 'Male',   Mainhand: 'TotallyMadeUpItem' }),
  ];

  const abilityUnits = [
    makeUnit({
      Name: 'Test_LancerCharges',
      Class: 'Lancer', Gender: 'Male', Mainhand: spearItem,
      ClassSkills: ['Charge+1', 'Charge+2', 'Charge+3', 'Charge+4', 'Charge+5', 'Charge+7', 'Charge+10', 'Charge+20'],
    }),
    makeUnit({
      Name: 'Test_WizardMagic',
      Class: 'Wizard', Gender: 'Female', Mainhand: rodItem,
      ClassSkills: ['Fire', 'Fire 2', 'Fire 3', 'Fire 4', 'Bolt', 'Bolt 2', 'Bolt 3', 'Bolt 4', 'Ice', 'Ice 2', 'Ice 3', 'Ice 4', 'Flare'],
    }),
    makeUnit({
      Name: 'Test_ChemistItems',
      Class: 'Chemist', Gender: 'Male',
      ClassSkills: ['Potion', 'Hi-Potion', 'X-Potion', 'Elixir', 'Phoenix Down'],
    }),
  ];

  // Distribute units across the team slots. The bracket section will show
  // these as the QF matchups (with empty winners), and the team-grid section
  // below will show them as scrollable rosters.
  const mid = Math.ceil(weaponUnits.length / 2);
  return {
    ID: 'TEST',
    Teams: {
      red:      { Units: weaponUnits.slice(0, mid) },
      blue:     { Units: weaponUnits.slice(mid)    },
      green:    { Units: edgeUnits                 },
      yellow:   { Units: abilityUnits              },
      white:    { Units: [] },
      black:    { Units: [] },
      purple:   { Units: [] },
      brown:    { Units: [] },
      champion: { Units: [] },
    },
    Maps:     ['', '', '', '', '', '', '', ''],
    Winners:  [],
    Entrants: [],
    Snubs:    [],
  };
}
